/**
 * Sales Uploads — admin dashboard.
 *
 * Two tabs:
 *  1. Uploads Dashboard — consolidated, filterable tables aggregated from
 *     auto-extracted Vision-OCR rows of every screenshot uploaded via a
 *     public link.
 *  2. Public Links — admin CRUD for time-bound, password-gated upload links.
 *
 * All data lives in MongoDB via /api/sales-uploads endpoints.
 */
import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Upload, Link2, Copy, ExternalLink, Plus, Calendar, Lock, Eye, EyeOff,
  Filter, X, FileText, TrendingUp, Activity, Search, Trash2, BarChart3, Layers, Download, RefreshCw, AlertTriangle,
  IndianRupee, ChevronDown,
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import { toast } from 'sonner';

const REPORT_TYPES = [
  { id: 'lead_source',          label: 'Call Back Leads Report',           short: 'Call Back',     icon: TrendingUp,  color: 'violet'  },
  { id: 'daily_hot_conversion', label: 'Daily Hot Conversion (Ownerwise)', short: 'Daily Hot',     icon: Activity,    color: 'rose'    },
  { id: 'utr_ownerwise',        label: 'UTR Ownerwise',                    short: 'UTR',           icon: BarChart3,   color: 'emerald' },
  { id: 'hot_lead_distribution',label: 'Hot Lead Distribution (Monthly)',  short: 'Hot Lead Dist.',icon: FileText,    color: 'amber'   },
];

const REPORT_SCHEMA = {
  lead_source:          { columns: [{ key: 'period', label: 'Created Time', type: 'text' }, { key: 'lead_owner', label: 'Lead Owner', type: 'text' }, { key: 'count', label: 'Record Count', type: 'number', sum: true }], combineBy: ['_upload_date', 'period', 'lead_owner'] },
  daily_hot_conversion: { columns: [{ key: 'date',   label: 'Date',         type: 'text' }, { key: 'lead_owner', label: 'Lead Owner', type: 'text' }, { key: 'count', label: 'Record Count', type: 'number', sum: true }], combineBy: ['_upload_date', 'date', 'lead_owner'] },
  // UTR is snapshot data — sum across months per (upload_date, lead_owner)
  utr_ownerwise:        { columns: [{ key: 'lead_owner', label: 'Lead Owner', type: 'text' }, { key: 'period', label: 'Month', type: 'text' }, { key: 'count', label: 'Record Count', type: 'number', sum: true }], combineBy: ['_upload_date', 'lead_owner'], hideInCombined: ['period'] },
  hot_lead_distribution:{ columns: [{ key: 'period', label: 'Month', type: 'text' }, { key: 'lead_owner', label: 'Lead Owner', type: 'text' }, { key: 'count', label: 'Record Count', type: 'number', sum: true }], combineBy: ['_upload_date', 'period', 'lead_owner'] },
};

const COLOR_BG = { violet: 'bg-violet-50 border-violet-200 text-violet-700', rose: 'bg-rose-50 border-rose-200 text-rose-700', emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700', amber: 'bg-amber-50 border-amber-200 text-amber-700' };

// ── Public-link manager tab ────────────────────────────────
function PublicLinksTab({ links, onChanged }) {
  const [open, setOpen] = useState(false);
  const initial = { label: 'Sales Team — Daily Reports', password: '', notes: '' };
  const [form, setForm] = useState(initial);
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const openCreate = () => { setForm(initial); setOpen(true); };

  const onSave = async () => {
    if (!form.password.trim()) return toast.error('Password is required');
    if (!form.label.trim()) return toast.error('Label is required');
    setBusy(true);
    try {
      await api.post('/sales-uploads/links', form);
      toast.success('Public link created');
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create link');
    } finally { setBusy(false); }
  };

  const onToggleActive = async (link) => {
    try {
      await api.patch(`/sales-uploads/links/${link.id}`, { is_active: !link.is_active });
      onChanged();
    } catch { toast.error('Failed to update'); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Revoke this link permanently? Already-uploaded data is kept.')) return;
    try {
      await api.delete(`/sales-uploads/links/${id}`);
      toast.success('Link revoked');
      onChanged();
    } catch { toast.error('Failed to delete'); }
  };

  const linkUrlOf = (slug) => `${window.location.origin}/u/${slug}`;
  const copyUrl = (slug) => { navigator.clipboard.writeText(linkUrlOf(slug)); toast.success('URL copied'); };
  const copyShareKit = (link) => {
    const url = linkUrlOf(link.slug);
    const text = `Sales Reports Upload Portal\n\nURL: ${url}\nPassword: (check Sales Command Center > Sales Uploads admin)\n\nUpload your daily Zoho screenshots here — they're auto-extracted into the consolidated dashboard.`;
    navigator.clipboard.writeText(text);
    toast.success('Share kit copied — paste into your message app');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Each link is gated by a unique URL slug + password. Already-uploaded data is preserved when a link is revoked.</p>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700" data-testid="new-public-link">
          <Plus className="w-3.5 h-3.5 mr-2" /> New public link
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(links || []).map(link => {
          const active = link.is_active;
          return (
            <Card key={link.id} className="border" data-testid={`public-link-${link.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-slate-800 truncate">{link.label || 'Untitled link'}</p>
                    <Badge variant="outline" className={`mt-1 text-[10px] ${active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      {link.is_active ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onToggleActive(link)} className="h-7 px-2 text-[10px]" title="Toggle active">{link.is_active ? 'Disable' : 'Enable'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(link.id)} className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" title="Revoke"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div><span className="text-slate-500">Slug</span><p className="font-mono font-semibold text-slate-700 truncate">{link.slug}</p></div>
                  <div><span className="text-slate-500">Uploads</span><p className="font-mono font-semibold text-slate-700">{link.used_count ?? 0}</p></div>
                  <div><span className="text-slate-500">By</span><p className="font-semibold text-slate-700 truncate">{link.created_by_name || '—'}</p></div>
                </div>

                <div className="flex items-center gap-1 pt-2 border-t border-slate-100">
                  <Input value={linkUrlOf(link.slug)} readOnly className="h-7 text-[10px] font-mono bg-slate-50" data-testid={`link-url-${link.id}`} />
                  <Button size="sm" variant="ghost" onClick={() => copyUrl(link.slug)} className="h-7 px-2 flex-shrink-0" title="Copy URL"><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => copyShareKit(link)} className="h-7 px-2 flex-shrink-0 text-[10px]" title="Copy share kit">Kit</Button>
                  <a href={linkUrlOf(link.slug)} target="_blank" rel="noopener noreferrer" className="inline-flex h-7 px-2 items-center text-violet-600 hover:bg-violet-50 rounded-sm flex-shrink-0" title="Open"><ExternalLink className="w-3 h-3" /></a>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {(links || []).length === 0 && <Card className="col-span-full p-8 text-center text-slate-400 text-sm">No public links yet — create one to share with your sales team.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="public-link-dialog" aria-describedby="public-link-dialog-desc">
          <DialogHeader>
            <DialogTitle>New public link</DialogTitle>
            <p id="public-link-dialog-desc" className="text-xs text-slate-500">Create a password-gated upload URL for your sales team.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Label</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. May 2026 — Sales Daily Reports" className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Password (sales team will need this)</Label>
              <div className="relative">
                <Input type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="h-9 text-sm font-mono pr-8" autoFocus />
                <button onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" type="button">
                  {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Internal notes (optional)</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Issued to Ritu's team" className="h-9 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="save-public-link">
              {busy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}Create link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Uploads dashboard tab ──────────────────────────────────
function DashboardTab({ uploads, refresh }) {
  const [reportFilter, setReportFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);

  // ── Filtering ────────────────────────────────────────────
  const filtered = useMemo(() => {
    return uploads.filter(u => {
      const d = (u.uploaded_at || '').slice(0, 10);
      if (reportFilter !== 'all' && u.report_type !== reportFilter) return false;
      if (ownerFilter !== 'all' && u.owner_filter !== ownerFilter) return false;
      if (d < dateFrom || d > dateTo) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${u.uploader_name || ''} ${u.owner_filter || ''} ${u.notes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [uploads, reportFilter, ownerFilter, dateFrom, dateTo, search]);

  // Card counts (across full date range, regardless of card filter)
  const cardCounts = useMemo(() => {
    const counts = {};
    REPORT_TYPES.forEach(t => { counts[t.id] = 0; });
    uploads.forEach(u => {
      const d = (u.uploaded_at || '').slice(0, 10);
      if (d < dateFrom || d > dateTo) return;
      if (ownerFilter !== 'all' && u.owner_filter !== ownerFilter) return;
      counts[u.report_type] = (counts[u.report_type] || 0) + 1;
    });
    return counts;
  }, [uploads, dateFrom, dateTo, ownerFilter]);

  // Tag every extracted row with its source upload metadata
  const consolidated = useMemo(() => {
    const byType = {};
    REPORT_TYPES.forEach(t => { byType[t.id] = []; });
    filtered.forEach(u => {
      (u.extracted_rows || []).forEach(r => {
        byType[u.report_type] = byType[u.report_type] || [];
        byType[u.report_type].push({
          ...r,
          _upload_id: u.id,
          _upload_date: (u.uploaded_at || '').slice(0, 10),
          _uploader: u.uploader_name,
          _uploaded_at: u.uploaded_at,
          _status: u.status,
        });
      });
    });
    return byType;
  }, [filtered]);

  const ownersInData = useMemo(() => {
    const s = new Set(uploads.map(u => u.owner_filter).filter(Boolean));
    return Array.from(s).sort();
  }, [uploads]);

  const failed = filtered.filter(u => u.status === 'failed');

  const clearFilters = () => { setReportFilter('all'); setOwnerFilter('all'); setSearch(''); };
  const hasFilters = reportFilter !== 'all' || ownerFilter !== 'all' || !!search;

  return (
    <div className="space-y-4">
      {/* Card-wise filter — top row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {REPORT_TYPES.map(t => {
          const Icon = t.icon;
          const active = reportFilter === t.id;
          return (
            <button key={t.id} onClick={() => setReportFilter(active ? 'all' : t.id)} data-testid={`card-${t.id}`}
              className={`text-left p-3 rounded-md border-2 transition-all ${active ? COLOR_BG[t.color] + ' shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
              <div className="flex items-center gap-2 mb-2"><Icon className={`w-4 h-4 ${active ? `text-${t.color}-700` : 'text-slate-500'}`} /><p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t.short}</p></div>
              <p className="text-2xl font-bold tabular-nums text-slate-800">{cardCounts[t.id] || 0}</p>
              <p className="text-[10px] text-slate-500">{cardCounts[t.id] === 1 ? 'upload' : 'uploads'} in range</p>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <Card className="border p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="filter-date-from" />
          <span className="text-xs text-slate-400">→</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid="filter-date-to" />
        </div>
        <div className="h-5 w-px bg-slate-200" />
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="h-8 text-xs w-44" data-testid="filter-owner"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {ownersInData.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search uploader / owner / notes…" className="h-8 text-xs pl-7" />
        </div>
        {hasFilters && <Button size="sm" variant="ghost" onClick={clearFilters} className="h-8 text-xs text-slate-500"><X className="w-3 h-3 mr-1" />Clear</Button>}
        <span className="ml-auto text-xs text-slate-500 font-mono">{filtered.length} / {uploads.length}</span>
      </Card>

      {failed.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 flex items-center gap-2 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span><strong>{failed.length}</strong> upload(s) failed Vision OCR.</span>
            <span className="text-amber-700">Click the upload card → Re-extract to retry.</span>
          </CardContent>
        </Card>
      )}

      {/* Consolidated tables */}
      <div className="space-y-6">
        {REPORT_TYPES.filter(t => reportFilter === 'all' || reportFilter === t.id).map(t => (
          <ConsolidatedReportSection
            key={t.id}
            reportType={t}
            rows={consolidated[t.id] || []}
            onPreview={setPreview}
            refresh={refresh}
          />
        ))}
        {REPORT_TYPES.every(t => (consolidated[t.id] || []).length === 0) && (
          <Card className="p-12 text-center text-slate-400">
            <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No extracted data matches the current filters.</p>
            <p className="text-xs mt-1">Create a public link in the Public Links tab and share it with your sales team to start collecting screenshots.</p>
          </Card>
        )}
      </div>

      <UploadPreviewDialog upload={preview} onClose={() => setPreview(null)} refresh={refresh} />
    </div>
  );
}

// ── Inside Sales Incentive panel (Daily Hot Conversion only) ─
const inr = (n) => `₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;

function DailyHotIncentivePanel() {
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/sales-uploads/incentive/daily-hot', { params: { month } })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => toast.error('Failed to load incentive data'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  const t = data?.totals;
  return (
    <div className="rounded-lg border-2 border-rose-200 bg-white overflow-hidden" data-testid="inside-sales-incentive-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 bg-rose-50/70 hover:bg-rose-100/60 transition text-left" data-testid="incentive-panel-toggle">
        <IndianRupee className="w-3.5 h-3.5 text-rose-700" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-rose-900">Inside Sales Incentive</p>
          <p className="text-[10px] text-rose-700/70">Day total ≥ {data?.target ?? 12} leads → total × ₹{data?.rate ?? 75} · below target or no upload → −₹{data?.penalty_amount ?? 150}/day (Sundays without data skipped)</p>
        </div>
        {t && <span className={`text-sm font-extrabold tabular-nums ${t.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="incentive-month-net">{t.net < 0 ? '−' : '+'}{inr(t.net)}</span>}
        <input
          type="month"
          value={month}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => e.target.value && setMonth(e.target.value)}
          className="text-[11px] border border-rose-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
          data-testid="incentive-month-picker"
        />
        <ChevronDown className={`w-3.5 h-3.5 text-rose-400 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>

      {open && (loading ? (
        <p className="text-[11px] text-slate-400 text-center py-6">Loading…</p>
      ) : !data ? null : (
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="rounded border border-rose-100 bg-rose-50/40 p-2"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Net Payable · {format(parseISO(`${month}-01`), 'MMM yyyy')}</p><p className={`text-lg font-extrabold ${t.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="incentive-kpi-net">{t.net < 0 ? '−' : ''}{inr(t.net)}</p></div>
            <div className="rounded border border-rose-100 bg-rose-50/40 p-2"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Earned (met days)</p><p className="text-lg font-extrabold text-slate-800">{inr(t.earned)}</p></div>
            <div className="rounded border border-rose-100 bg-rose-50/40 p-2"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Penalties</p><p className="text-lg font-extrabold text-rose-700">−{inr(t.penalty)} <span className="text-[10px] font-semibold text-slate-400">({t.missed_days + t.no_data_days} day{t.missed_days + t.no_data_days === 1 ? '' : 's'})</span></p></div>
            <div className="rounded border border-rose-100 bg-rose-50/40 p-2"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Days met / counted</p><p className="text-lg font-extrabold text-slate-800">{t.met_days} / {t.days_counted}</p></div>
          </div>

          {data.days.length === 0 ? (
            <p className="text-[11px] text-slate-400 text-center py-4">No working days to evaluate in this month yet.</p>
          ) : (
            <div className="rounded border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase">Date</TableHead>
                    <TableHead className="text-[10px] uppercase">Lead Owner</TableHead>
                    <TableHead className="text-[10px] uppercase text-right">Record Count</TableHead>
                    <TableHead className="text-[10px] uppercase">Day Target ≥ {data.target}</TableHead>
                    <TableHead className="text-[10px] uppercase text-right">Calculation</TableHead>
                    <TableHead className="text-[10px] uppercase text-right">Incentive</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.days.map((day) => (
                    <Fragment key={day.date}>
                      {day.owners.map((o, i) => (
                        <TableRow key={`${day.date}-${o.lead_owner}-${i}`}>
                          <TableCell className="py-1 text-xs text-slate-400">{i === 0 ? format(parseISO(day.date), 'dd MMM yyyy') : ''}</TableCell>
                          <TableCell className="py-1 text-xs text-slate-600">{o.lead_owner}</TableCell>
                          <TableCell className="py-1 text-xs text-right tabular-nums text-slate-600">{o.count}</TableCell>
                          <TableCell className="py-1" /><TableCell className="py-1" /><TableCell className="py-1" />
                        </TableRow>
                      ))}
                      <TableRow className={`border-t-2 ${day.has_data ? 'bg-rose-50/60 border-rose-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`incentive-day-${day.date}`}>
                        <TableCell className="py-1.5 text-xs font-bold text-rose-900" colSpan={day.has_data ? 2 : 1}>
                          {day.has_data ? `Subtotal — ${format(parseISO(day.date), 'dd MMM yyyy')}` : format(parseISO(day.date), 'dd MMM yyyy')}
                        </TableCell>
                        {!day.has_data && <TableCell className="py-1.5 text-xs italic text-slate-400">No data uploaded</TableCell>}
                        <TableCell className="py-1.5 text-xs font-bold text-right tabular-nums">{day.has_data ? day.total : '—'}</TableCell>
                        <TableCell className="py-1.5">
                          <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold ${day.met ? 'bg-emerald-100 text-emerald-700' : day.has_data ? 'bg-rose-100 text-rose-700' : 'bg-slate-200 text-slate-600'}`}>
                            {day.met ? `Met (${day.total} ≥ ${data.target})` : day.has_data ? `Missed (${day.total} < ${data.target})` : 'No data'}
                          </span>
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right text-slate-500 tabular-nums">{day.met ? `${day.total} × ₹${data.rate}` : 'penalty'}</TableCell>
                        <TableCell className={`py-1.5 text-xs font-bold text-right tabular-nums ${day.incentive >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{day.incentive < 0 ? '−' : '+'}{inr(day.incentive)}</TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                  <TableRow className="bg-rose-900 hover:bg-rose-900" data-testid="incentive-month-total-row">
                    <TableCell className="py-2 text-xs font-bold text-white" colSpan={2}>MONTH TOTAL — {format(parseISO(`${month}-01`), 'MMMM yyyy')} · Inside Sales Incentive</TableCell>
                    <TableCell className="py-2 text-xs font-bold text-white text-right tabular-nums">{t.total_leads}</TableCell>
                    <TableCell className="py-2 text-[10px] font-semibold text-rose-100">{t.met_days} met · {t.missed_days + t.no_data_days} missed</TableCell>
                    <TableCell className="py-2 text-xs font-bold text-rose-100 text-right tabular-nums">{inr(t.earned)} − {inr(t.penalty)}</TableCell>
                    <TableCell className="py-2 text-sm font-extrabold text-white text-right tabular-nums">{t.net < 0 ? '−' : '+'}{inr(t.net)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-[10px] text-slate-400 leading-relaxed">Duplicate rows across uploads are de-duplicated — for each (date, lead owner) the latest upload's count wins. Working days with no upload count as missed (−₹{data.penalty_amount}); Sundays are skipped unless data exists.</p>
        </div>
      ))}
    </div>
  );
}

// ── Consolidated table per report type ─────────────────────
function ConsolidatedReportSection({ reportType, rows, onPreview, refresh }) {
  const t = reportType;
  const schema = REPORT_SCHEMA[t.id];
  const [showCombined, setShowCombined] = useState(true);

  // Columns visible in COMBINED view: prepend Upload Date, hide any cols schema marks as hidden (e.g. UTR drops 'period')
  const combinedCols = useMemo(() => {
    if (!schema) return [];
    const hide = schema.hideInCombined || [];
    return [
      { key: '_upload_date', label: 'Upload Date', type: 'text' },
      ...schema.columns.filter(c => !hide.includes(c.key)),
    ];
  }, [schema]);

  const combined = useMemo(() => {
    if (!schema) return [];
    const groupKeys = schema.combineBy || ['_upload_date', ...schema.columns.filter(c => !c.sum).map(c => c.key)];
    const sumKeys = schema.columns.filter(c => c.sum).map(c => c.key);
    const map = new Map();
    rows.forEach(r => {
      const k = JSON.stringify(groupKeys.map(g => r[g] ?? ''));
      if (!map.has(k)) {
        const base = {};
        groupKeys.forEach(g => { base[g] = r[g] ?? ''; });
        sumKeys.forEach(s => { base[s] = 0; });
        map.set(k, base);
      }
      const acc = map.get(k);
      sumKeys.forEach(s => { acc[s] = (acc[s] || 0) + Number(r[s] || 0); });
    });
    // Sort newest date first, then by first non-date group key
    return Array.from(map.values()).sort((a, b) =>
      String(b._upload_date || '').localeCompare(String(a._upload_date || '')) ||
      String(a[groupKeys[1]] || '').localeCompare(String(b[groupKeys[1]] || ''))
    );
  }, [rows, schema]);

  if (!schema) return null;

  const allCols = [
    { key: '_upload_date', label: 'Upload Date', type: 'text', width: '110px' },
    ...schema.columns,
    { key: '_uploader', label: 'Uploader', type: 'text', width: '130px', readonly: true },
  ];

  const updateCell = async (uploadId, rowId, key, value) => {
    if (key.startsWith('_')) return;
    // Fetch full upload, mutate the matching row, then PATCH /rows
    try {
      const { data: up } = await api.get(`/sales-uploads/${uploadId}`);
      const next = (up.extracted_rows || []).map(r => r.id === rowId ? { ...r, [key]: value } : r);
      await api.patch(`/sales-uploads/${uploadId}/rows`, { rows: next });
      refresh();
    } catch { toast.error('Failed to save edit'); }
  };

  const deleteRow = async (uploadId, rowId) => {
    try {
      const { data: up } = await api.get(`/sales-uploads/${uploadId}`);
      const next = (up.extracted_rows || []).filter(r => r.id !== rowId);
      await api.patch(`/sales-uploads/${uploadId}/rows`, { rows: next });
      toast.success('Row removed');
      refresh();
    } catch { toast.error('Failed to delete row'); }
  };

  const downloadCsv = () => {
    const cols = allCols;
    const headers = cols.map(c => `"${c.label}"`).join(',');
    const sortedRows = rows.slice().sort((a, b) => (a._upload_date || '').localeCompare(b._upload_date || '') || (a._uploader || '').localeCompare(b._uploader || ''));
    const dataRows = sortedRows.map(r => cols.map(c => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers, ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${t.id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <Card className={`border-2 ${COLOR_BG[t.color]} opacity-60`}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-white/70 flex items-center justify-center"><t.icon className="w-4 h-4" /></div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-700">{t.label}</p>
              <p className="text-[11px] text-slate-500">No uploads in the selected date range yet.</p>
            </div>
          </CardContent>
        </Card>
        {t.id === 'daily_hot_conversion' && <DailyHotIncentivePanel />}
      </div>
    );
  }

  const dates = Array.from(new Set(rows.map(r => r._upload_date))).sort().reverse();
  const sourceUploaders = Array.from(new Set(rows.map(r => r._uploader).filter(Boolean))).sort();
  const sortedRows = rows.slice().sort((a, b) => (b._upload_date || '').localeCompare(a._upload_date || '') || (a._uploader || '').localeCompare(b._uploader || ''));

  return (
    <Card className={`border-2 ${COLOR_BG[t.color]}`} data-testid={`section-${t.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60">
          <div className="w-9 h-9 rounded-md bg-white/70 flex items-center justify-center"><t.icon className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800">{t.label}</p>
            <p className="text-[10px] text-slate-500">{rows.length} rows extracted · {dates.length} day{dates.length === 1 ? '' : 's'} · {sourceUploaders.length} uploader{sourceUploaders.length === 1 ? '' : 's'}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowCombined(!showCombined)} className="h-7 text-[11px]" data-testid={`toggle-combined-${t.id}`}>
            <Layers className="w-3 h-3 mr-1" />{showCombined ? 'Show raw rows' : 'Show combined'}
          </Button>
          <Button size="sm" variant="ghost" onClick={downloadCsv} className="h-7 text-[11px]" data-testid={`csv-${t.id}`}><Download className="w-3 h-3 mr-1" />CSV</Button>
        </div>

        {t.id === 'daily_hot_conversion' && <DailyHotIncentivePanel />}

        {showCombined ? (
          <div className="rounded border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  {combinedCols.map(c => <TableHead key={c.key} className="text-[10px] uppercase">{c.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // Build subtotals per Upload Date and interleave subtotal rows
                  // after the last row of each upload-date block. No grand total.
                  const out = [];
                  let prevDate = null;
                  const flushSubtotal = (rowsForDate, dateKey) => {
                    if (!rowsForDate.length) return;
                    const subtotal = {};
                    combinedCols.forEach(c => {
                      if (c.sum) subtotal[c.key] = rowsForDate.reduce((s, r) => s + Number(r[c.key] || 0), 0);
                    });
                    out.push(
                      <TableRow key={`sub-${dateKey}`} className="bg-slate-100/80 border-t-2 border-slate-300">
                        {combinedCols.map((c, i) => (
                          <TableCell key={c.key} className={`py-1.5 text-xs font-bold ${c.sum ? 'tabular-nums text-right' : ''}`}>
                            {c.sum
                              ? subtotal[c.key]
                              : i === 0
                                ? `Subtotal — ${dateKey ? format(parseISO(dateKey), 'dd MMM yyyy') : '—'}`
                                : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  };
                  let bucket = [];
                  combined.forEach((r, i) => {
                    const d = r._upload_date || '';
                    if (prevDate !== null && d !== prevDate) {
                      flushSubtotal(bucket, prevDate);
                      bucket = [];
                    }
                    bucket.push(r);
                    prevDate = d;
                    out.push(
                      <TableRow key={i}>
                        {combinedCols.map(c => (
                          <TableCell key={c.key} className={`py-1.5 text-xs ${c.sum ? 'tabular-nums font-semibold text-right' : ''}`}>
                            {c.key === '_upload_date' && r[c.key] ? format(parseISO(r[c.key]), 'dd MMM yyyy') : r[c.key]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  });
                  // Flush the last bucket
                  flushSubtotal(bucket, prevDate);
                  return out;
                })()}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  {allCols.map(c => <TableHead key={c.key} className="text-[10px] uppercase" style={{ width: c.width }}>{c.label}</TableHead>)}
                  <TableHead className="w-16 text-right pr-2">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map(r => (
                  <TableRow key={`${r._upload_id}-${r.id}`} data-testid={`row-${r.id}`}>
                    {allCols.map(c => (
                      <TableCell key={c.key} className="p-1">
                        {c.readonly || c.key === '_upload_date' ? (
                          <span className="text-xs text-slate-600 px-2">{c.key === '_upload_date' && r[c.key] ? format(parseISO(r[c.key]), 'dd MMM') : r[c.key]}</span>
                        ) : (
                          <input
                            type={c.type === 'number' ? 'number' : 'text'}
                            defaultValue={r[c.key] ?? ''}
                            onBlur={(e) => {
                              const v = c.type === 'number' ? Number(e.target.value) : e.target.value;
                              if (v !== r[c.key]) updateCell(r._upload_id, r.id, c.key, v);
                            }}
                            className={`w-full px-2 py-1 text-xs rounded border border-transparent hover:border-slate-300 focus:border-violet-500 focus:bg-slate-50 outline-none bg-transparent ${c.sum ? 'text-right tabular-nums' : ''}`}
                          />
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-right pr-1">
                      <div className="inline-flex gap-0.5">
                        <button onClick={() => onPreview && onPreview({ id: r._upload_id })} title="View source screenshot" className="h-6 w-6 flex items-center justify-center rounded text-slate-500 hover:text-violet-700 hover:bg-violet-50" data-testid={`row-view-${r.id}`}>
                          <Eye className="w-3 h-3" />
                        </button>
                        <button onClick={() => deleteRow(r._upload_id, r.id)} title="Delete row" className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`row-delete-${r.id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Upload preview dialog (lazy-fetches the screenshot) ────
function UploadPreviewDialog({ upload, onClose, refresh }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!upload) { setFull(null); return; }
    let alive = true;
    api.get(`/sales-uploads/${upload.id}`, { params: { include_image: true } })
      .then(({ data }) => { if (alive) setFull(data); })
      .catch(() => toast.error('Failed to load upload'));
    return () => { alive = false; };
  }, [upload]);

  const reExtract = async () => {
    if (!full) return;
    setBusy(true);
    try {
      await api.post(`/sales-uploads/${full.id}/re-extract`);
      toast.success('Re-extracted');
      refresh();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!full) return;
    if (!window.confirm('Delete this upload and its extracted rows?')) return;
    try {
      await api.delete(`/sales-uploads/${full.id}`);
      toast.success('Upload deleted');
      refresh();
      onClose();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={!!upload} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {full ? REPORT_TYPES.find(r => r.id === full.report_type)?.label : 'Loading…'}
            {full && <span className="text-xs font-normal text-slate-500 ml-2">· {full.uploader_name} · {full.uploaded_at?.slice(0, 10)}</span>}
          </DialogTitle>
        </DialogHeader>
        {full ? (
          <div className="space-y-3">
            <img src={`data:${full.mime_type};base64,${full.screenshot_b64}`} alt="" className="w-full max-h-[60vh] object-contain rounded border border-slate-200" />
            {full.notes && <p className="text-sm text-slate-600 italic">"{full.notes}"</p>}
            {full.status === 'failed' && (
              <Card className="border-rose-200 bg-rose-50"><CardContent className="p-3 text-xs text-rose-800"><strong>OCR failed:</strong> {full.extraction_error || 'unknown error'}</CardContent></Card>
            )}
            <p className="text-[10px] text-slate-400">Uploaded at {full.uploaded_at && format(parseISO(full.uploaded_at), 'dd MMM yyyy, HH:mm')}</p>
          </div>
        ) : <p className="text-sm text-slate-400">Loading screenshot…</p>}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onDelete} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
          <Button size="sm" onClick={reExtract} disabled={busy} className="bg-violet-600 hover:bg-violet-700">{busy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}Re-extract</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────
export default function SalesUploadsPage() {
  const [links, setLinks] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: u }] = await Promise.all([
        api.get('/sales-uploads/links'),
        api.get('/sales-uploads', { params: { limit: 1000 } }),
      ]);
      setLinks(l);
      setUploads(u);
    } catch (e) {
      toast.error('Failed to load Sales Uploads data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="sales-uploads-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Upload className="w-6 h-6 text-violet-600" /> Sales Uploads
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px] uppercase tracking-wider">Live</Badge>
          </h1>
          <p className="text-xs text-slate-500 mt-1">Time-bound public links. Sales team uploads daily Zoho screenshots; Vision-OCR auto-extracts them into the consolidated tables below.</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="text-xs"><RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Uploads Dashboard <Badge className="ml-2 text-[9px]" variant="secondary">{uploads.length}</Badge></TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links"><Link2 className="w-3.5 h-3.5 mr-1.5" />Public Links <Badge className="ml-2 text-[9px]" variant="secondary">{links.length}</Badge></TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab uploads={uploads} refresh={refresh} /></TabsContent>
        <TabsContent value="links"><PublicLinksTab links={links} onChanged={refresh} /></TabsContent>
      </Tabs>
    </div>
  );
}
